import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, HandCoins, Landmark, Mail, Plus, Printer, RotateCcw, Search, Send, Trash2, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { invalidateIncidents, InvoiceStatusBadge } from '../../components/device-history';
import { useToast } from '../../components/toast';
import { Alert, Badge, Button, Card, CardHeader, cn, ConfirmDialog, ErrorState, Field, Input, Modal, PageHeader, PageLoader, Textarea } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatDateTime, formatMoney, formatUtcDate } from '../../lib/format';
import { INCIDENT_TYPE_LABEL, lineTotal, type DamageInvoice, type Guardian, type GuardianLookup } from '../../lib/incidents';

type StatusChange = 'PAID' | 'WAIVED' | 'VOID' | 'REOPEN';

const STATUS_DONE: Record<StatusChange, string> = { PAID: 'Marked paid', WAIVED: 'Invoice waived', VOID: 'Invoice voided', REOPEN: 'Invoice reopened' };

/** A damage invoice: a draft to edit, the invoice as the family sees it, and sending, posting, and payment. */
export function InvoiceDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dirty, setDirty] = useState(false);
  const [confirming, setConfirming] = useState<'email' | 'blackbaud' | 'paid' | 'delete' | null>(null);
  const [closing, setClosing] = useState<'WAIVED' | 'VOID' | null>(null);
  const [reason, setReason] = useState('');

  const query = useQuery({ queryKey: ['invoice', id], queryFn: () => api.get<DamageInvoice>(`/invoices/${id}`) });
  const onError = (err: unknown) => toast.error(errorMessage(err));
  const settle = (invoice: DamageInvoice, message: string) => {
    queryClient.setQueryData(['invoice', id], invoice);
    invalidateIncidents(queryClient);
    setConfirming(null);
    setClosing(null);
    toast.success(message);
  };

  const email = useMutation({
    mutationFn: () => api.post<DamageInvoice>(`/invoices/${id}/email`),
    onSuccess: (invoice) => {
      settle(invoice, 'Invoice emailed');
      if (invoice.failed?.length) toast.error(`It didn’t go to ${invoice.failed.join('; ')}`);
    },
    onError,
  });
  const post = useMutation({
    mutationFn: () => api.post<DamageInvoice>(`/invoices/${id}/blackbaud`),
    onSuccess: (invoice) => settle(invoice, 'Posted to Blackbaud Billing Management'),
    onError,
  });
  const status = useMutation({
    mutationFn: (body: { status: StatusChange; reason?: string }) => api.post<DamageInvoice>(`/invoices/${id}/status`, body),
    onSuccess: (invoice, body) => settle(invoice, STATUS_DONE[body.status]),
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/invoices/${id}`),
    onSuccess: () => {
      invalidateIncidents(queryClient);
      toast.success('Draft deleted');
      navigate(`/incidents/${query.data!.incident.id}`);
    },
    onError,
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const invoice = query.data;
  const { permissions } = invoice;
  const unsaved = dirty ? 'Save your changes first' : null;
  const emailBlocked =
    unsaved ??
    (!invoice.email.ready
      ? invoice.email.reason
      : invoice.billToEmails.length === 0
        ? 'Add a parent or guardian’s email first'
        : invoice.total <= 0
          ? 'Add a charge first'
          : null);
  const postBlocked = unsaved ?? (!invoice.blackbaud.ready ? invoice.blackbaud.reason : invoice.total <= 0 ? 'Add a charge first' : null);
  const payment =
    invoice.status === 'PAID'
      ? `Paid ${formatDateTime(invoice.paidAt)}`
      : invoice.status === 'WAIVED' || invoice.status === 'VOID'
        ? `${invoice.status === 'WAIVED' ? 'Waived' : 'Voided'} ${formatDateTime(invoice.closedAt)}${invoice.closedReason ? `: ${invoice.closedReason}` : ''}`
        : invoice.status === 'DRAFT'
          ? 'A draft. The family doesn’t see it until it’s emailed or posted to Blackbaud.'
          : invoice.overdue
            ? `Overdue since ${formatUtcDate(invoice.dueOn)}`
            : `Due ${formatUtcDate(invoice.dueOn)}`;

  return (
    <>
      <div className="print:hidden">
        <PageHeader
          back={{ to: `/incidents/${invoice.incident.id}`, label: `Incident #${invoice.incident.number}` }}
          title={`Invoice #${invoice.number}`}
          meta={
            <>
              <InvoiceStatusBadge status={invoice.status} />
              {invoice.overdue && <Badge tone="red">Overdue</Badge>}
              <span className="text-sm text-slate-500">
                {invoice.studentName} · {formatMoney(invoice.total)}
              </span>
            </>
          }
          actions={
            <Button icon={<Printer />} onClick={() => window.print()}>
              Print
            </Button>
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3 print:block">
        <div className="space-y-6 lg:col-span-2">
          {permissions.edit && <InvoiceEditor invoice={invoice} onDirtyChange={setDirty} />}
          <InvoiceDocument invoice={invoice} />
        </div>

        <div className="space-y-6 print:hidden">
          <Card>
            <CardHeader title="Sending" />
            <div className="space-y-5 p-5 text-sm">
              <section className="space-y-1">
                <p className="flex items-center gap-1.5 font-medium text-slate-900">
                  <Mail className="size-4 text-slate-400" /> Email to parents
                </p>
                {invoice.emailedAt ? (
                  <p className="text-slate-600">
                    Sent {formatDateTime(invoice.emailedAt)} to {invoice.emailedTo.join(', ')}
                  </p>
                ) : (
                  <p className="text-slate-500">Not emailed yet.</p>
                )}
                {invoice.email.ready ? (
                  invoice.email.mailbox && <p className="text-xs text-slate-500">From {invoice.email.mailbox}</p>
                ) : (
                  <p className="text-xs text-amber-700">{invoice.email.reason}</p>
                )}
                {permissions.email && (
                  <Button size="sm" className="mt-2" icon={<Send />} disabled={!!emailBlocked} title={emailBlocked ?? undefined} onClick={() => setConfirming('email')}>
                    {invoice.emailedAt ? 'Email again' : 'Email to parents'}
                  </Button>
                )}
              </section>

              <section className="space-y-1">
                <p className="flex items-center gap-1.5 font-medium text-slate-900">
                  <Landmark className="size-4 text-slate-400" /> Blackbaud Billing Management
                </p>
                {invoice.blackbaudPostedAt ? (
                  <p className="text-slate-600">
                    Posted {formatDateTime(invoice.blackbaudPostedAt)}
                    {invoice.blackbaudImportId && <span className="block font-mono text-xs break-all text-slate-500">Import {invoice.blackbaudImportId}</span>}
                  </p>
                ) : (
                  <p className="text-slate-500">Not posted.</p>
                )}
                {!invoice.blackbaudPostedAt && invoice.blackbaud.reason && <p className="text-xs text-amber-700">{invoice.blackbaud.reason}</p>}
                {invoice.blackbaud.demo && <p className="text-xs text-slate-500">Blackbaud is in demo mode, so nothing is really billed.</p>}
                {permissions.postToBlackbaud && (
                  <Button size="sm" className="mt-2" icon={<Landmark />} disabled={!!postBlocked} title={postBlocked ?? undefined} onClick={() => setConfirming('blackbaud')}>
                    Post to Blackbaud
                  </Button>
                )}
              </section>
            </div>
          </Card>

          <Card>
            <CardHeader title="Payment" />
            <div className="space-y-3 p-5 text-sm">
              <p className={cn(invoice.overdue ? 'text-red-700' : 'text-slate-700')}>{payment}</p>
              <div className="flex flex-wrap gap-2">
                {permissions.markPaid && (
                  <Button size="sm" icon={<Check />} onClick={() => setConfirming('paid')}>
                    Mark paid
                  </Button>
                )}
                {permissions.waive && (
                  <Button size="sm" variant="ghost" icon={<HandCoins />} onClick={() => setClosing('WAIVED')}>
                    Waive
                  </Button>
                )}
                {permissions.void && (
                  <Button size="sm" variant="ghost" icon={<Ban />} onClick={() => setClosing('VOID')}>
                    Void
                  </Button>
                )}
                {permissions.reopen && (
                  <Button size="sm" variant="ghost" icon={<RotateCcw />} loading={status.isPending} onClick={() => status.mutate({ status: 'REOPEN' })}>
                    Reopen
                  </Button>
                )}
                {permissions.delete && (
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setConfirming('delete')}>
                    Delete draft
                  </Button>
                )}
              </div>
              {!can('ADMIN') && invoice.status !== 'VOID' && (
                <p className="text-xs text-slate-500">Administrators email invoices, post them to Blackbaud, and record payment.</p>
              )}
              <p className="text-xs text-slate-500">
                Created by {invoice.createdByName} · {formatDate(invoice.createdAt)}
              </p>
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirming === 'email'}
        onClose={() => setConfirming(null)}
        onConfirm={() => email.mutate()}
        title="Email this invoice?"
        description={`Invoice #${invoice.number} for ${formatMoney(invoice.total)} goes to ${invoice.billToEmails.join(', ')}${invoice.email.mailbox ? `, from ${invoice.email.mailbox}` : ''}.`}
        confirmLabel="Email invoice"
        tone="primary"
        loading={email.isPending}
      />
      <ConfirmDialog
        open={confirming === 'blackbaud'}
        onClose={() => setConfirming(null)}
        onConfirm={() => post.mutate()}
        title="Post to Blackbaud Billing Management?"
        description={`A ${formatMoney(invoice.total)} charge is added to ${invoice.studentName}’s account${invoice.blackbaud.demo ? ' (demo mode: nothing is really billed)' : ''}. Edvora can’t remove it afterwards; that’s done in Blackbaud.`}
        confirmLabel="Post charge"
        tone="primary"
        loading={post.isPending}
      />
      <ConfirmDialog
        open={confirming === 'paid'}
        onClose={() => setConfirming(null)}
        onConfirm={() => status.mutate({ status: 'PAID' })}
        title="Mark this invoice paid?"
        description={`Records that ${formatMoney(invoice.total)} was paid for invoice #${invoice.number}.`}
        confirmLabel="Mark paid"
        tone="primary"
        loading={status.isPending}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        onClose={() => setConfirming(null)}
        onConfirm={() => remove.mutate()}
        title="Delete this draft?"
        description="It hasn’t gone to the family, so it can be deleted."
        confirmLabel="Delete draft"
        loading={remove.isPending}
      />
      <Modal
        open={!!closing}
        onClose={() => setClosing(null)}
        title={closing === 'VOID' ? `Void invoice #${invoice.number}?` : `Waive invoice #${invoice.number}?`}
        description={closing === 'VOID' ? 'A voided invoice can’t be reopened.' : 'The family no longer owes it. You can reopen it later.'}
        footer={
          <>
            <Button onClick={() => setClosing(null)}>Cancel</Button>
            <Button
              variant={closing === 'VOID' ? 'danger' : 'primary'}
              loading={status.isPending}
              onClick={() => closing && status.mutate({ status: closing, reason }, { onSuccess: () => setReason('') })}
            >
              {closing === 'VOID' ? 'Void invoice' : 'Waive invoice'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Reason" hint="Kept in the invoice’s history">
            <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} />
          </Field>
          {closing === 'VOID' && invoice.blackbaudPostedAt && (
            <Alert tone="amber">Its charge stays in Billing Management. Reverse it in Blackbaud too.</Alert>
          )}
        </div>
      </Modal>
    </>
  );
}

interface EditorForm {
  billToName: string;
  emails: string;
  issuedOn: string;
  dueOn: string;
  notes: string;
  items: { description: string; quantity: string; unitAmount: string }[];
}

const formFrom = (invoice: DamageInvoice): EditorForm => ({
  billToName: invoice.billToName ?? '',
  emails: invoice.billToEmails.join(', '),
  issuedOn: invoice.issuedOn,
  dueOn: invoice.dueOn,
  notes: invoice.notes ?? '',
  items: invoice.items.map((item) => ({ description: item.description, quantity: String(item.quantity), unitAmount: item.unitAmount.toFixed(2) })),
});

const splitEmails = (value: string) =>
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

function InvoiceEditor({ invoice, onDirtyChange }: { invoice: DamageInvoice; onDirtyChange(dirty: boolean): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => formFrom(invoice));
  const [lookup, setLookup] = useState<GuardianLookup | null>(null);
  const saved = formFrom(invoice);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  useEffect(() => {
    setForm(formFrom(invoice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.updatedAt]);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const find = useMutation({
    mutationFn: () => api.get<GuardianLookup>(`/invoices/${invoice.id}/guardians`),
    onSuccess: setLookup,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const save = useMutation({
    mutationFn: () =>
      api.patch<DamageInvoice>(`/invoices/${invoice.id}`, {
        billToName: form.billToName,
        billToEmails: splitEmails(form.emails),
        issuedOn: form.issuedOn,
        dueOn: form.dueOn,
        notes: form.notes,
        items: form.items.map((item) => ({ description: item.description, quantity: Number(item.quantity) || 0, unitAmount: Number(item.unitAmount) || 0 })),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['invoice', invoice.id], updated);
      invalidateIncidents(queryClient);
      toast.success('Invoice saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const addGuardian = (guardian: Guardian) => {
    if (!guardian.email) return;
    setForm({
      ...form,
      billToName: form.billToName || guardian.name,
      emails: [...new Set([...splitEmails(form.emails), guardian.email])].join(', '),
    });
  };
  const setItem = (index: number, patch: Partial<EditorForm['items'][number]>) =>
    setForm({ ...form, items: form.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  const total = lineTotal(form.items.map((item) => ({ quantity: Number(item.quantity) || 0, unitAmount: Number(item.unitAmount) || 0 })));

  return (
    <Card className="print:hidden">
      <CardHeader
        title="Edit the draft"
        description="The family doesn’t see the invoice until it’s emailed or posted to Blackbaud."
        actions={
          <Button size="sm" variant="primary" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
            Save changes
          </Button>
        }
      />
      <div className="space-y-6 p-5">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">Bill to</h3>
            <Button size="sm" variant="ghost" icon={<Search />} loading={find.isPending} onClick={() => find.mutate()}>
              Find parents in Blackbaud
            </Button>
          </div>
          {lookup &&
            (lookup.guardians.length > 0 ? (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {lookup.guardians.map((guardian) => {
                  const added = !!guardian.email && splitEmails(form.emails).includes(guardian.email);
                  return (
                    <li key={`${guardian.name}-${guardian.email}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">
                          {guardian.name}
                          {guardian.relationship && <span className="font-normal text-slate-500"> · {guardian.relationship}</span>}
                          {guardian.billing && (
                            <Badge tone="brand" className="ml-2">
                              Pays tuition
                            </Badge>
                          )}
                        </p>
                        <p className="truncate text-xs text-slate-500">{guardian.email ?? 'No email in Blackbaud'}</p>
                      </div>
                      <Button
                        size="sm"
                        variant={added ? 'ghost' : 'secondary'}
                        icon={added ? <Check /> : <UserPlus />}
                        disabled={!guardian.email || added}
                        onClick={() => addGuardian(guardian)}
                      >
                        {added ? 'Added' : 'Add'}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Alert tone="amber">{lookup.reason}</Alert>
            ))}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input value={form.billToName} onChange={(event) => setForm({ ...form, billToName: event.target.value })} placeholder="e.g. Jordan and Casey Rivera" maxLength={200} />
            </Field>
            <Field label="Email addresses" hint="Separate addresses with commas">
              <Input value={form.emails} onChange={(event) => setForm({ ...form, emails: event.target.value })} placeholder="parent@example.com" />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">Charges</h3>
          <div className="space-y-2">
            <div className="hidden grid-cols-[1fr_4.5rem_6.5rem_2rem] gap-2 text-xs font-medium text-slate-500 sm:grid">
              <span>Description</span>
              <span>Qty</span>
              <span>Price</span>
            </div>
            {form.items.map((item, index) => (
              <div key={index} className="grid grid-cols-[1fr_4.5rem_6.5rem_2rem] items-center gap-2">
                <Input
                  value={item.description}
                  onChange={(event) => setItem(index, { description: event.target.value })}
                  placeholder="What the charge is for"
                  maxLength={200}
                  aria-label={`Charge ${index + 1}`}
                />
                <Input type="number" min={1} max={1000} value={item.quantity} onChange={(event) => setItem(index, { quantity: event.target.value })} aria-label="Quantity" />
                <Input type="number" min={0} step="0.01" value={item.unitAmount} onChange={(event) => setItem(index, { unitAmount: event.target.value })} aria-label="Price" />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<X />}
                  disabled={form.items.length === 1}
                  onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}
                  aria-label="Remove charge"
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus />}
              onClick={() => setForm({ ...form, items: [...form.items, { description: '', quantity: '1', unitAmount: '' }] })}
            >
              Add a charge
            </Button>
            <p className="text-sm font-semibold text-slate-900">Total {formatMoney(total)}</p>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <Field label="Invoice date">
            <Input type="date" value={form.issuedOn} onChange={(event) => setForm({ ...form, issuedOn: event.target.value })} />
          </Field>
          <Field label="Due">
            <Input type="date" value={form.dueOn} min={form.issuedOn} onChange={(event) => setForm({ ...form, dueOn: event.target.value })} />
          </Field>
        </section>

        <Field label="Note to the family" hint="Shown on the invoice and in the email">
          <Textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} maxLength={2000} />
        </Field>
      </div>
    </Card>
  );
}

/** The invoice as the family sees it, laid out to print. */
function InvoiceDocument({ invoice }: { invoice: DamageInvoice }) {
  const stamp =
    invoice.status === 'PAID'
      ? { label: 'Paid', className: 'border-green-600 text-green-700' }
      : invoice.status === 'WAIVED'
        ? { label: 'Waived', className: 'border-blue-600 text-blue-700' }
        : invoice.status === 'VOID'
          ? { label: 'Void', className: 'border-red-600 text-red-700' }
          : null;
  const asset = invoice.incident.asset;
  const model = [asset.manufacturer, asset.model].filter(Boolean).join(' ');
  const label = 'text-xs font-medium tracking-wide text-slate-500 uppercase';

  return (
    <Card className="overflow-hidden print:rounded-none print:border-0 print:shadow-none">
      <div className="relative space-y-8 p-6 sm:p-10 print:p-0">
        <header className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            {invoice.school.logoUrl && <img src={invoice.school.logoUrl} alt="" className="size-12 object-contain" />}
            <div>
              <p className="text-lg font-semibold text-slate-900">{invoice.school.name}</p>
              {invoice.school.contactEmail && <p className="text-sm text-slate-500">{invoice.school.contactEmail}</p>}
            </div>
          </div>
          <div className="sm:text-right">
            <p className="text-2xl font-semibold tracking-tight text-slate-900">Invoice #{invoice.number}</p>
            <p className="text-sm text-slate-500">Issued {formatUtcDate(invoice.issuedOn)}</p>
            <p className="text-sm font-medium text-slate-900">Due {formatUtcDate(invoice.dueOn)}</p>
            {stamp && (
              <div className={cn('mt-3 inline-block -rotate-6 rounded-md border-4 px-3 py-0.5 text-xl font-black tracking-widest uppercase opacity-70', stamp.className)}>
                {stamp.label}
              </div>
            )}
          </div>
        </header>

        <div className="grid gap-6 sm:grid-cols-2 print:grid-cols-2">
          <div>
            <p className={label}>Bill to</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{invoice.billToName || `The family of ${invoice.studentName}`}</p>
            {invoice.billToEmails.map((address) => (
              <p key={address} className="text-sm break-all text-slate-600">
                {address}
              </p>
            ))}
          </div>
          <div>
            <p className={label}>For</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{invoice.studentName}</p>
            <p className="text-sm text-slate-600">
              {INCIDENT_TYPE_LABEL[invoice.incident.type]} on {formatDate(invoice.incident.occurredAt)}
            </p>
            <p className="text-sm text-slate-600">
              {asset.name} · Asset tag {asset.assetTag}
              {asset.serialNumber && ` · Serial ${asset.serialNumber}`}
            </p>
            {model && model !== asset.name && <p className="text-sm text-slate-500">{model}</p>}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[28rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className={cn(label, 'py-2')}>Description</th>
                <th className={cn(label, 'py-2 text-right')}>Qty</th>
                <th className={cn(label, 'py-2 text-right')}>Price</th>
                <th className={cn(label, 'py-2 text-right')}>Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoice.items.map((item) => (
                <tr key={item.id}>
                  <td className="py-2.5 pr-4 text-slate-800">{item.description}</td>
                  <td className="py-2.5 text-right text-slate-600">{item.quantity}</td>
                  <td className="py-2.5 text-right text-slate-600">{formatMoney(item.unitAmount)}</td>
                  <td className="py-2.5 text-right font-medium text-slate-900">{formatMoney(item.quantity * item.unitAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-900">
                <td colSpan={3} className="pt-3 text-right font-semibold text-slate-900">
                  Total due
                </td>
                <td className="pt-3 text-right text-lg font-semibold text-slate-900">{formatMoney(invoice.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {invoice.notes && (
          <div>
            <p className={label}>Note</p>
            <p className="mt-1 text-sm whitespace-pre-line text-slate-700">{invoice.notes}</p>
          </div>
        )}
        {invoice.school.paymentInstructions && (
          <div>
            <p className={label}>How to pay</p>
            <p className="mt-1 text-sm whitespace-pre-line text-slate-700">{invoice.school.paymentInstructions}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
